import { orderSourceLabel } from '@/utils/orderSource';
/**
 * OrderDetailModal - Shows full order properties in a modal overlay.
 *
 * Used when drilling into a duplicate match from DuplicateWarningPopover.
 * Fetches order data by ID and displays it in a clean read-only format.
 * Click X or outside to close.
 */
import React, { useState, useLayoutEffect, useMemo, useRef, useCallback, useSyncExternalStore } from 'react';
import { X, Loader2, Package } from 'lucide-react';
import StatusBadge from '@/components/StatusBadge';
import { getOrder } from '@/api/orders';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import { getActiveSessionRevision, subscribeActiveSessionRevision, getAuthTokenForRequest } from '@/api/authenticatedClient';

const formatDate = (d) => {
  if (!d) return 'N/A';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return d; }
};

const OrderDetailModal = ({ orderId, onClose }) => {
  const { sessionBinding } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision);
  const scope = useMemo(() => ({ orderId, binding: sessionBinding, revision, api: API }), [orderId, sessionBinding, revision]);
  const liveScope = useRef(scope);
  liveScope.current = scope;
  const attempt = useRef(null);
  const closedScope = useRef(null);
  const [result, setResult] = useState(null);
  const retire = useCallback(() => {
    if (attempt.current) {
      attempt.current.active = false;
      attempt.current.controller.abort();
    }
  }, []);
  const read = useCallback(async () => {
    if (liveScope.current !== scope || closedScope.current === scope) return;
    // Retire synchronously: two Retry clicks cannot both publish, even if a
    // transport ignores abort or settles before the next React render.
    retire();
    const controller = new AbortController();
    const url = `${scope.api}/orders/${scope.orderId}`;
    const token = getAuthTokenForRequest(url);
    const current = { scope, controller, active: true };
    attempt.current = current;
    const owns = () => current.active && attempt.current === current
      && liveScope.current === scope && scope.revision === getActiveSessionRevision()
      && token === getAuthTokenForRequest(url);
    const publish = (status, order = null) => {
      if (owns()) setResult({ scope, status, order });
    };
    if (!token || !scope.binding?.user || scope.binding.revision !== scope.revision) {
      publish('unauthorized');
      return;
    }
    if (!scope.orderId || scope.api === null) {
      publish('error');
      return;
    }
    publish('loading');
    try {
      const found = await getOrder(scope.orderId, {
        signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
      });
      if (!found || found.id !== scope.orderId) {
        publish('error');
        return;
      }
      publish('ready', found);
    } catch (error) {
      const status = error?.response?.status;
      publish(status === 404 ? 'missing' : status === 401 ? 'unauthorized' : status === 403 ? 'forbidden' : 'error');
    }
  }, [scope, retire]);

  useLayoutEffect(() => {
    closedScope.current = null;
    void read();
    return retire;
  }, [read, retire]);

  const close = () => {
    closedScope.current = scope;
    retire();
    setResult({ scope, status: 'closed', order: null });
    onClose();
  };
  // Never render a previous identity while waiting for effect cleanup/start.
  const view = result?.scope === scope && revision === getActiveSessionRevision()
    ? result : { status: 'loading', order: null };
  const order = view.order;
  if (view.status === 'closed') return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4"
      onClick={close}
      data-testid="order-detail-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="order-detail-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-stone-500" />
            <span className="text-sm font-semibold text-stone-900">
              {order ? `Order #${order.internal_order_number || order.id.slice(0, 8)}` : 'Order Details'}
            </span>
          </div>
          <button
            onClick={close}
            aria-label="Close order details"
            className="text-stone-400 hover:text-stone-600 transition-colors"
            data-testid="order-detail-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {view.status === 'loading' ? (
            <div className="flex items-center justify-center py-10 text-stone-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : view.status === 'missing' ? (
            <p className="text-center text-stone-500 py-10">Order not found</p>
          ) : view.status === 'unauthorized' || view.status === 'forbidden' ? (
            <p role="alert" className="text-center text-stone-500 py-10">
              {view.status === 'unauthorized' ? 'Sign in again to view this order.' : 'You do not have permission to view this order.'}
            </p>
          ) : view.status === 'error' ? (
            <div role="alert" className="text-center py-10">
              <p>Order could not be loaded. Please retry.</p>
              <button className="mt-3 rounded-lg border px-3 py-1.5" onClick={read} data-testid="order-detail-modal-retry">Retry</button>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <Row label="Status"><StatusBadge status={order.status || 'designed'} size="sm" showIcon /></Row>
              <Row label="Recipient">{order.recipient_name || 'N/A'}</Row>
              <Row label="Address">
                {[order.recipient_street_address, order.recipient_street_address_2].filter(Boolean).join(', ')}
                {order.recipient_city && `, ${order.recipient_city}`}
                {order.recipient_state && `, ${order.recipient_state}`}
                {order.recipient_zip_code && ` ${order.recipient_zip_code}`}
              </Row>
              <Row label="Phone">{order.recipient_phone || 'N/A'}</Row>
              <Row label="Delivery Date">{formatDate(order.delivery_date)}</Row>
              <Row label="Accepted">{formatDate(order.order_accepted_date)}</Row>
              <Row label="Source">{orderSourceLabel(order, 'N/A')}</Row>
              <Row label="Product">{order.product_description || 'N/A'}</Row>
              <Row label="Total">{order.total_price ? `$${Number(order.total_price).toFixed(2)}` : 'N/A'}</Row>
              {order.card_message && (
                <Row label="Card Message">
                  <span className="italic">"{order.card_message}"</span>
                </Row>
              )}
              {order.special_instructions && (
                <Row label="Special Instructions">{order.special_instructions}</Row>
              )}
              {order.sender_name && <Row label="Sender">{order.sender_name}</Row>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex gap-3">
    <span className="text-stone-500 w-28 flex-shrink-0 text-right">{label}</span>
    <span className="text-stone-900 flex-1">{children}</span>
  </div>
);

export default OrderDetailModal;
